import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Table, Button, Switch, Tag, Space, Popconfirm,
  message, Input, Modal, Form, Tooltip,
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  SearchOutlined, EyeOutlined, StarOutlined, ReloadOutlined,
  ExportOutlined, ImportOutlined, CopyOutlined,
} from "@ant-design/icons";
import {
  fetchFlows, deleteFlow, toggleFlow, createFlow,
  importFlow, duplicateFlow,
} from "../../../../../core/data/redux/slices/WabaAutomationSlice";
import api from "../../../../../core/axios/axiosInstance";
import TableSkeleton from "../../../../../core/common/TableSkeleton";

const FlowList = ({ basePath = "/whatsapp/automation/flows" }) => {
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const { flows, loading } = useSelector((s) => s.wabaAutomation);

  const [isInitialLoad, setIsInitialLoad] = useState(flows.length === 0);

  const [search, setSearch]           = useState("");
  const [createModal, setCreateModal] = useState(false);
  const [form]                        = Form.useForm();

  // Import / Export / Duplicate
  const importInputRef              = useRef(null);
  const [exportingId, setExportingId]   = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const [importing, setImporting]       = useState(false);

  // Submissions drawer
  const [submissionsModal, setSubmissionsModal] = useState(false);
  const [submissions, setSubmissions]           = useState([]);
  const [submissionFlow, setSubmissionFlow]     = useState(null);
  const [subLoading, setSubLoading]             = useState(false);

  // CSAT drawer
  const [csatModal, setCsatModal]   = useState(false);
  const [csatFlow, setCsatFlow]     = useState(null);
  const [csatData, setCsatData]     = useState([]);
  const [csatAvg, setCsatAvg]       = useState(null);
  const [csatLoading, setCsatLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchFlows()).finally(() => setIsInitialLoad(false));
  }, [dispatch]);

  const handleDelete = async (id) => {
    await dispatch(deleteFlow(id)).unwrap();
    message.success("Deleted");
  };

  const handleToggle = async (id) => {
    await dispatch(toggleFlow(id)).unwrap();
  };

  const handleCreate = async () => {
    const values = await form.validateFields();
    const result = await dispatch(createFlow({ name: values.name, description: values.description })).unwrap();
    setCreateModal(false);
    navigate(`${basePath}/${result._id}`);
  };

  const handleExport = async (flow) => {
    setExportingId(flow._id);
    try {
      const res = await api.get(`/api/waba/automation/flows/${flow._id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${flow.name.replace(/[^a-z0-9]/gi, "_")}_automation.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { message.error("Export failed"); }
    finally { setExportingId(null); }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!data.nodes) { message.error("Invalid automation export file"); return; }
      await dispatch(importFlow(data)).unwrap();
      message.success("Automation imported successfully");
    } catch (err) {
      message.error(err?.message || "Import failed — invalid file");
    } finally { setImporting(false); }
  };

  const handleDuplicate = async (flow) => {
    setDuplicatingId(flow._id);
    try {
      await dispatch(duplicateFlow(flow._id)).unwrap();
      message.success(`Copied "${flow.name}"`);
    } catch { message.error("Duplicate failed"); }
    finally { setDuplicatingId(null); }
  };

  const openSubmissions = async (flow) => {
    setSubmissionFlow(flow);
    setSubLoading(true);
    setSubmissionsModal(true);
    try {
      const res = await api.get(`/api/waba/automation/flows/${flow._id}/submissions`);
      setSubmissions(res.data.data || []);
    } catch { message.error("Failed to load submissions"); }
    finally { setSubLoading(false); }
  };

  const openCsat = async (flow) => {
    setCsatFlow(flow);
    setCsatLoading(true);
    setCsatModal(true);
    setCsatData([]);
    setCsatAvg(null);
    try {
      const res = await api.get(`/api/waba/automation/flows/${flow._id}/csat`);
      setCsatData(res.data.data || []);
      setCsatAvg(res.data.average);
    } catch { message.error("Failed to load CSAT ratings"); }
    finally { setCsatLoading(false); }
  };

  const STAR_LABELS = { 5: "Excellent", 4: "Good", 3: "Average", 2: "Below average", 1: "Poor" };
  const STAR_COLORS = { 5: "#52c41a", 4: "#73d13d", 3: "#faad14", 2: "#fa8c16", 1: "#ff4d4f" };

  const csatColumns = [
    { title: "Phone", dataIndex: "contactPhone", key: "phone", width: 150 },
    {
      title: "Rating", dataIndex: "rating", key: "rating", width: 160,
      render: (v) => (
        <span style={{ color: STAR_COLORS[v], fontWeight: 600 }}>
          {"⭐".repeat(v)} <span style={{ fontSize: 11, color: "#6b7280" }}>{STAR_LABELS[v]}</span>
        </span>
      ),
    },
    {
      title: "Score", dataIndex: "rating", key: "score", width: 80,
      render: (v) => <Tag color={STAR_COLORS[v]} style={{ color: "#fff", fontWeight: 700 }}>{v}/5</Tag>,
    },
    { title: "Date", dataIndex: "createdAt", key: "date", render: (v) => new Date(v).toLocaleString() },
  ];

  const filtered = flows.filter((f) =>
    f.name?.toLowerCase().includes(search.toLowerCase())
  );

  const subColumns = [
    { title: "Phone",   dataIndex: "contactPhone", key: "phone" },
    { title: "Type",    dataIndex: "contactType",  key: "type",   render: (v) => <Tag>{v}</Tag> },
    { title: "Status",  dataIndex: "status",       key: "status", render: (v) => <Tag color={v === "completed" ? "green" : "orange"}>{v}</Tag> },
    {
      title: "Answers", dataIndex: "answers", key: "answers",
      render: (answers) => (
        <div>
          {(answers || []).map((a, i) => (
            <div key={i} style={{ fontSize: 12 }}>
              <span style={{ color: "#6b7280" }}>{a.question || `Step ${i+1}`}:</span>{" "}
              <strong>{String(a.answer || "—")}</strong>
            </div>
          ))}
        </div>
      ),
    },
    { title: "Date", dataIndex: "completedAt", key: "date", render: (v) => v ? new Date(v).toLocaleString() : "—" },
  ];

  const columns = [
    { title: "Name", dataIndex: "name", key: "name", render: (v) => <strong>{v}</strong> },
    {
      title: "Active", key: "active",
      render: (_, r) => <Switch checked={r.isActive} onChange={() => handleToggle(r._id)} size="small" />,
    },
    { title: "Created", dataIndex: "createdAt", key: "created", render: (v) => new Date(v).toLocaleDateString() },
    {
      title: "Actions", key: "actions",
      render: (_, r) => (
        <Space>
          <Button icon={<EditOutlined />}   size="small" onClick={() => navigate(`${basePath}/${r._id}`)}>Edit</Button>
          <Button icon={<EyeOutlined />}    size="small" onClick={() => openSubmissions(r)}>Submissions</Button>
          <Button icon={<StarOutlined />}   size="small" onClick={() => openCsat(r)} style={{ color: "#f59e0b", borderColor: "#f59e0b" }}>CSAT</Button>
          <Tooltip title="Export automation as JSON">
            <Button
              icon={<ExportOutlined />}
              size="small"
              loading={exportingId === r._id}
              onClick={() => handleExport(r)}
            />
          </Tooltip>
          <Tooltip title="Duplicate automation">
            <Button
              icon={<CopyOutlined />}
              size="small"
              loading={duplicatingId === r._id}
              onClick={() => handleDuplicate(r)}
            />
          </Tooltip>
          <Popconfirm title="Delete this automation?" onConfirm={() => handleDelete(r._id)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0 }}>Automations</h3>
          <p style={{ margin: 0, color: "#6b7280", fontSize: 13 }}>Build automated conversation flows</p>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} loading={loading} onClick={() => dispatch(fetchFlows({ forceRefresh: true }))}>
            Refresh
          </Button>
          <Button
            icon={<ImportOutlined />}
            loading={importing}
            onClick={() => importInputRef.current?.click()}
          >
            Import
          </Button>
          <input
            ref={importInputRef}
            type="file"
            accept=".json,application/json"
            style={{ display: "none" }}
            onChange={handleImportFile}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModal(true)}>
            Create Automation
          </Button>
        </Space>
      </div>

      <Input
        prefix={<SearchOutlined />}
        placeholder="Search automations"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ maxWidth: 320, marginBottom: 16 }}
      />

      {isInitialLoad ? (
        <TableSkeleton
          rows={8}
          rowHeight={42}
          columns={[
            { width: 180, type: "text" },
            { width: 60,  type: "badge" },
            { width: 110, type: "text" },
            { width: 200, type: "action" },
          ]}
        />
      ) : (
      <Table
        dataSource={filtered}
        columns={columns}
        rowKey="_id"
        loading={false}
        size="small"
        pagination={{ pageSize: 10 }}
      />
      )}

      {/* Create modal */}
      <Modal
        title="New Automation"
        open={createModal}
        onOk={handleCreate}
        onCancel={() => setCreateModal(false)}
        okText="Create & Open Builder"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Flow Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Lead Qualification" />
          </Form.Item>
          <Form.Item name="description" label="Description (optional)">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* CSAT Ratings modal */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <span>⭐ CSAT Ratings — {csatFlow?.name}</span>
            {csatAvg !== null && (
              <span style={{
                background: "#fef3c7", border: "1px solid #fcd34d",
                borderRadius: 20, padding: "2px 12px", fontSize: 13, fontWeight: 700, color: "#92400e",
              }}>
                Avg {csatAvg} / 5
              </span>
            )}
          </div>
        }
        open={csatModal}
        onCancel={() => setCsatModal(false)}
        footer={null}
        width={700}
      >
        {csatData.length === 0 && !csatLoading && (
          <div style={{ textAlign: "center", padding: "32px 0", color: "#9ca3af" }}>
            No ratings yet for this flow.
          </div>
        )}
        <Table
          dataSource={csatData}
          columns={csatColumns}
          rowKey="_id"
          loading={csatLoading}
          size="small"
          pagination={{ pageSize: 10 }}
        />
      </Modal>

      {/* Submissions modal */}
      <Modal
        title={`Submissions — ${submissionFlow?.name}`}
        open={submissionsModal}
        onCancel={() => setSubmissionsModal(false)}
        footer={null}
        width={800}
      >
        <Table
          dataSource={submissions}
          columns={subColumns}
          rowKey="_id"
          loading={subLoading}
          size="small"
          pagination={{ pageSize: 10 }}
        />
      </Modal>
    </div>
  );
};

export default FlowList;
